/**
 * Conversion and diagnostic events for the paid-search landing page.
 *
 * Every event here goes through `gtag('event', ...)` on the page, and only when gtag is actually
 * there. `analytics.ts` puts gtag on the page only after the visitor has consented to analytics and
 * a valid measurement id is configured, and removes it on withdrawal, so this module needs no
 * consent logic of its own: no gtag, no event. Events are never sent to the OfficeA11y API; only
 * the attribution below is, by the sign-up and scan forms.
 *
 * Event names and roles follow the campaign build sheet (MKTG-37):
 *
 *   lp_cta_click, faq_expand   diagnostic
 *   form_start                 early signal; once per form per page view
 *   form_error                 diagnostic; `field_name`
 *   scan_submitted             early signal, imported as secondary (observe-only), never primary;
 *                              `file_type`
 *   sign_up                    PRIMARY conversion
 *   purchase                   PRIMARY conversion, counted once per click in Ads; `value`,
 *                              `currency`, `transaction_id`
 *
 * Attribution: UTM parameters and `gclid` are read off the landing URL and held in memory for the
 * life of the page. They are attached to the three conversion events (which only exist under
 * analytics consent), and sent with the registration and public-scan requests so OfficeA11y's own
 * server records the campaign on the new account (OA-137) whatever the consent state.
 *
 * They are deliberately never written to browser storage. The site's cookie preferences describe
 * the marketing category as the one used to "measure the effectiveness of marketing campaigns",
 * and the banner promises "No marketing or advertising trackers"; keeping a campaign tag on the
 * device before consent would fall in that category. Memory is enough: the landing page reaches
 * the sign-up and scan forms by in-app navigation, so the values are still there at submit. A
 * reload or a new tab loses them, which costs some attribution and nothing else.
 *
 * Usage:
 *   captureAttribution();                       // once, at app start
 *   trackEvent('lp_cta_click', { cta_location: 'hero' });
 *   trackConversion('scan_submitted', { file_type: 'pdf' });
 */

interface GtagWindow extends Window {
  gtag?: (...args: unknown[]) => void;
}

/** Query parameters kept as attribution. `gclid` is Google Ads auto-tagging. */
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
] as const;

/**
 * Where an earlier release kept attribution in `sessionStorage`. Only ever removed now, so a tab
 * that loaded the old build leaves nothing behind.
 */
export const LEGACY_ATTRIBUTION_STORAGE_KEY = 'oa11y_attribution';

type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];
export type Attribution = Partial<Record<AttributionParam, string>>;

/**
 * Keeps a hostile or accidental giant query string out of event payloads and requests. A longer
 * value is dropped rather than cut: a truncated click ID is a wrong click ID. Google documents no
 * fixed gclid length, so this is kept well above the ~100 characters seen in practice; the API
 * caps at 255 (its column width).
 */
const MAX_VALUE_LENGTH = 255;

/** The first tagged landing of this page load. In memory only; see the module comment. */
let captured: Attribution | null = null;

/**
 * Records the landing URL's UTM parameters and `gclid`, once per page load.
 *
 * First landing wins: a later in-app navigation never overwrites what the first tagged URL
 * recorded. A landing with none of the parameters records nothing, so a visitor who arrives
 * organically and later follows a tagged link is still captured.
 */
export const captureAttribution = (search: string = globalThis.location.search): void => {
  try {
    globalThis.sessionStorage.removeItem(LEGACY_ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Storage unavailable. There is then nothing of ours in it either.
  }
  if (captured) {
    return;
  }
  const query = new URLSearchParams(search);
  const found: Attribution = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const value = query.get(key)?.trim();
    if (value && value.length <= MAX_VALUE_LENGTH) {
      found[key] = value;
    }
  }
  if (Object.keys(found).length > 0) {
    captured = found;
  }
};

/** The captured attribution, as a fresh object the caller may keep or send. */
export const getAttribution = (): Attribution => ({ ...captured });

/** Forgets the captured attribution. For tests, which share one module instance per file. */
export const resetAttribution = (): void => {
  captured = null;
};

/** Whether gtag is on the page, which is to say analytics consent is in force. */
export const canTrack = (): boolean =>
  typeof (globalThis as unknown as GtagWindow).gtag === 'function';

type EventParams = Record<string, string | number | boolean | undefined>;

/** Sends one gtag event, or does nothing when gtag is not loaded. Never throws. */
export const trackEvent = (name: string, params: EventParams = {}): void => {
  const { gtag } = globalThis as unknown as GtagWindow;
  if (typeof gtag !== 'function') {
    return;
  }
  try {
    gtag('event', name, params);
  } catch {
    // A measurement failure must never break the page the visitor is using.
  }
};

/** Like `trackEvent`, with the session's UTM parameters and `gclid` attached. */
export const trackConversion = (name: string, params: EventParams = {}): void => {
  trackEvent(name, { ...getAttribution(), ...params });
};

/** The lowercase extension of a file name, without the dot; 'unknown' when there is none. */
export const fileTypeOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot !== -1 && dot < fileName.length - 1
    ? fileName.slice(dot + 1).toLowerCase()
    : 'unknown';
};
